import { PUBLIC_HOCKEYDATA_URL } from '$app/env/public';
import {
	HDDivisionType,
	type HDDivisionInfoResponse,
	type HDTeam
} from './types/responses/DivisionInfo.js';
import { defu } from 'defu';
import type { Season } from '#lib/schema-extension.js';
import type {
	HDGameReportResponse,
	HDGameReportResponseData,
	HDGoal,
	HDPenalty
} from './types/responses/GameReport.js';
import type { HDPlayerDetailsResponse } from './types/responses/PlayerDetails.js';
import type { HDExtraData } from './types/util/ExtraData.js';
import type {
	HDScheduleResponse,
	HDScheduleResponseData,
	HDScheduleResponseRow
} from './types/responses/Schedule.js';
import dayjs from 'dayjs';
import _ from 'lodash';
import type { HDStandingsResponse } from './types/responses/Standings.js';
import type { HDTeamDetailsResponse } from './types/responses/TeamDetails.js';
import type { HDKnockoutStageResponse } from './types/responses/KnockoutStage.js';

//region utils

export const calcTime = (time: string | number) => {
	if (typeof time === 'string') {
		time = parseInt(time);
	}
	const minutes = Math.floor(time / 60);
	const seconds = time - minutes * 60;
	return `${minutes < 10 ? '0' + minutes : minutes}:${seconds < 10 ? '0' + seconds : seconds}`;
};

export const getTeamImageUrl = (teamId?: number, divisionId?: string) => {
	return `${PUBLIC_HOCKEYDATA_URL}/img/icehockey/ebel/team-logos/${divisionId}/${teamId}.png`;
};

export const concatGoals = (game: HDGameReportResponseData) =>
	[
		...game.homeGoals.map(
			(goal: HDGoal): HDExtraData<HDGoal> => ({
				id: goal.guid,
				extraData: {
					team: game.gameData.homeTeamShortname
				},
				...goal
			})
		),
		...game.awayGoals.map(
			(goal: HDGoal): HDExtraData<HDGoal> => ({
				id: goal.guid,
				extraData: {
					team: game.gameData.awayTeamShortname
				},
				...goal
			})
		)
	].sort((a, b) => a.gameTime - b.gameTime);

export const concatPenalties = (game: HDGameReportResponseData) =>
	[
		...game.homePenalties.map(
			(penalty): HDExtraData<HDPenalty> => ({
				id: penalty.guid,
				extraData: {
					team: game.gameData.homeTeamShortname
				},
				...penalty
			})
		),
		...game.awayPenalties.map(
			(penalty): HDExtraData<HDPenalty> => ({
				id: penalty.guid,
				extraData: {
					team: game.gameData.awayTeamShortname
				},
				...penalty
			})
		)
	].sort((a, b) => a.gameTime - b.gameTime);

export const findOwnTeamId = (teams: HDTeam[]) => {
	return teams.find(
		(team) =>
			team.longname.toLowerCase().includes('tigers') ||
			team.longname.toLowerCase().includes('wildcats')
	)?.id;
};

export const positions = {
	G: 'Goalie',
	F: 'Stürmer',
	D: 'Verteidiger'
} as const;

//endregion

class HockeyData {
	private url: string;
	private fetchFn: typeof fetch;

	private defaultQueryOptions: Record<string, string> = {
		apiKey: 'c5feca83015f31ad67c1b28656439b66', // hardcoded i know, but browser devtools would find it anyways :)
		lang: 'de',
		referer: 'tigers-paternion.at'
	};

	constructor(url: string, fetchFn: typeof fetch) {
		this.url = url;
		this.fetchFn = fetchFn;
	}

	private async getRequest(path: string, options: Record<string, string>) {
		const queryOptions = new URLSearchParams(defu(options, this.defaultQueryOptions));
		// log.debug('hockeydata', 'getRequest', options, queryOptions.toString());
		const res = await this.fetchFn(`${this.url}${path}?${queryOptions.toString()}`);
		if (!(res instanceof Response)) {
			throw new Error('Invalid response');
		}
		return res.json();
	}

	public async getDivisionInfo({ seasonId, leagueId }: Season): Promise<HDDivisionInfoResponse> {
		// log.debug('hockeydata', 'getDivisionInfo', { seasonId, leagueId });
		return this.getRequest('/data/ebel/GetDivisionInfo', {
			leagueId: leagueId ?? '',
			seasonId: seasonId ?? '',
			id: seasonId ?? ''
		});
	}

	public async getGroupDivisionId(
		{ seasonId, leagueId }: Season,
		teamId?: string
	): Promise<string | undefined> {
		// log.debug('hockeydata', 'getGroupDivisionId', { seasonId, leagueId });
		const res = await this.getDivisionInfo({ seasonId, leagueId });
		if (res.data.divisionInfo.divisionType === HDDivisionType.GROUP) {
			return res.data.divisionInfo.id.toString();
		}
		const groups = res.data.divisions.filter((d) => d.divisionType === HDDivisionType.GROUP);
		const ownTeamId = teamId ? Number(teamId) : findOwnTeamId(res.data.teams);
		const groupDivision =
			groups.find((division) => ownTeamId !== undefined && division.teams.includes(ownTeamId)) ??
			groups[0];
		return groupDivision?.id.toString();
	}

	public async getSeasonDivisionId({ seasonId, leagueId }: Season): Promise<string | undefined> {
		// log.debug('hockeydata', 'getSeasonDivisionId', { seasonId, leagueId });
		const res = await this.getDivisionInfo({ seasonId, leagueId });
		const seasonDivision =
			res.data.divisionInfo.divisionType === HDDivisionType.SEASON
				? res.data.divisionInfo
				: res.data.divisions.find((d) => d.divisionType === HDDivisionType.SEASON);
		return seasonDivision?.id.toString();
	}

	public async getPlayoffDivisionId({ seasonId, leagueId }: Season): Promise<string | undefined> {
		// log.debug('hockeydata', 'getPlayoffDivisionId', { seasonId, leagueId });
		const res = await this.getDivisionInfo({ seasonId, leagueId });
		const playoffDivision = res.data.divisions.find(
			(d) => d.divisionType === HDDivisionType.PLAYOFF
		);
		return playoffDivision?.id.toString();
	}

	public async getGameReport(gameId: string): Promise<HDGameReportResponse> {
		// log.debug('hockeydata', 'getGameReport', gameId);
		return this.getRequest('/data/ebel/GetGameReport', { gameId });
	}

	public async getPlayerInfo(
		playerId: string,
		seasonId: string,
		divisionId: string
	): Promise<HDPlayerDetailsResponse> {
		// log.debug('hockeydata', 'getPlayerInfo', { playerId, seasonId, divisionId });
		return this.getRequest('/data/ebel/GetPlayerDetails', { playerId, seasonId, divisionId });
	}

	public async getSchedule({ leagueId, seasonId }: Season): Promise<HDScheduleResponse> {
		// log.debug('hockeydata', 'getSchedule', { leagueId, seasonId });
		const playoffDivisionId = await this.getPlayoffDivisionId({ leagueId, seasonId });
		const playoffGames = playoffDivisionId
			? this.getRequest('/data/ebel/Schedule', { divisionId: playoffDivisionId })
			: Promise.resolve({ data: { rows: [] } });
		const regularGames = this.getRequest('/data/ebel/Schedule', {
			divisionId: (await this.getGroupDivisionId({ leagueId, seasonId })) ?? ''
		});

		return Promise.all([playoffGames, regularGames]).then((res) => {
			const playoff = res[0] as HDScheduleResponse;
			const regular = res[1] as HDScheduleResponse;
			const data: HDScheduleResponseData = {
				rows: [...regular.data.rows, ...playoff.data.rows]
			};
			return { ...regular, data };
		});
	}

	public async getStandings({ leagueId, seasonId }: Season): Promise<HDStandingsResponse> {
		// log.debug('hockeydata', 'getStandings', { leagueId, seasonId });
		return this.getRequest('/data/ebel/Standings', {
			divisionId: (await this.getGroupDivisionId({ leagueId, seasonId })) ?? ''
		});
	}

	public async getNextMatch({
		seasonId,
		leagueId
	}: Season): Promise<HDScheduleResponseRow | undefined> {
		// log.debug('hockeydata', 'getNextmatch', { teamId, seasonId, leagueId });
		return this.getSchedule({ leagueId, seasonId }).then((res) => {
			const matches = res.data.rows.filter(
				(match) =>
					dayjs().isBefore(dayjs(match.scheduledDate.sortValue)) &&
					(match.homeTeamLongName.toLowerCase().includes('tigers') ||
						match.homeTeamLongName.toLowerCase().includes('wildcats') ||
						match.awayTeamLongName.toLowerCase().includes('tigers') ||
						match.awayTeamLongName.toLowerCase().includes('wildcats'))
			);
			_.sortBy(matches, (match) => match.scheduledDate.sortValue);
			return _.first(matches);
		});
	}

	public async getTeamDetails(
		{ leagueId, seasonId }: Season,
		teamId: string
	): Promise<HDTeamDetailsResponse> {
		// log.debug('hockeydata', 'getTeamDetails', { leagueId, seasonId, teamId });
		return this.getRequest('/data/ebel/GetTeamDetails', {
			divisionId: (await this.getGroupDivisionId({ leagueId, seasonId }, teamId)) ?? '',
			teamId
		});
	}

	public async getTeamImageUrl({ leagueId, seasonId }: Season, teamId: string) {
		// log.debug('hockeydata', 'getTeamImageUrl', { leagueId, seasonId, teamId });
		return getTeamImageUrl(
			parseInt(teamId),
			(await this.getGroupDivisionId({ leagueId, seasonId }, teamId)) ?? ''
		);
	}

	public async getKnockoutStage(divisionId: string): Promise<HDKnockoutStageResponse> {
		// log.debug('hockeydata', 'getKnockoutStage', { divisionId });
		return this.getRequest('/data/ebel/KnockoutStage', {
			divisionId
		});
	}
}

function getHockeydataInstance(fetchFn: typeof fetch) {
	const hockeydata = new HockeyData(PUBLIC_HOCKEYDATA_URL, fetchFn);
	return hockeydata;
}

export default getHockeydataInstance;
